import { ReactNode, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  RESTAURANT_USER_ROLE,
  useAuthUserRestaurant,
} from '@durma-soft/gros-sdk';
import { CompanyEmployee } from '@durma-soft/gros-sdk/dist/types/company';

import { Link } from '@/components/shared/link';
import { SearchInput } from '@/components/shared/search-input';
import { PrintButton } from '@/components/shared/print-button';
import { buttonVariants } from '@/components/shared/shadcn-ui/button';
import { DataTable, DataTableHTML } from '@/components/shared/data-table';
import { CompanyBreadcrumb } from '@/components/restaurant/companies/company-breadcrumb';
import { PrintEmployeeQrCardsButton } from '@/components/restaurant/companies/print-employee-qr-cards-button';
import { ImportCompanyEmployeesModal } from '@/components/restaurant/companies/import-company-employees-modal';
import { EditRestaurantCompanyEmployeeModal } from '@/components/restaurant/companies/edit-restaurant-company-employee-modal';
import { CreateRestaurantCompanyEmployeeModal } from '@/components/restaurant/companies/create-restaurant-company-employee-modal';

import { useTableSearchParams } from '@/hooks/use-table-search-params';
import { columns, printColumns } from '@/config/restaurant/company-employees';

import print from '@/lib/print/print';
import { cn } from '@/utils/helpers';

interface CompanyEmployeesPageProps {
  companyId: number;
  employeeId?: string;
  breadcrumbItems: { label: ReactNode; to: string }[];
  createTo: string;
  importTo: string;
  query: {
    data?: { data: CompanyEmployee[]; total_pages: number };
    isLoading: boolean;
    isPlaceholderData: boolean;
  };
  state: ReturnType<typeof useTableSearchParams>;
}

/** Shared body of the "all company employees" and "single company location
    employees" pages - they differ only in query, links and breadcrumbs */
export const CompanyEmployeesPage = ({
  companyId,
  employeeId,
  breadcrumbItems,
  createTo,
  importTo,
  query,
  state,
}: CompanyEmployeesPageProps) => {
  const { t } = useTranslation();

  const restaurant = useAuthUserRestaurant();
  const hasEditPermission = restaurant.role !== RESTAURANT_USER_ROLE.EMPLOYEE;

  const [memoizedColumns, memoizedPrintColumns] = useMemo(() => {
    return [columns(t), printColumns(t)];
  }, [t]);

  const { data, isLoading, isPlaceholderData } = query;
  const {
    pagination,
    setPagination,
    sorting,
    setSorting,
    searchInput,
    handleSearchChange,
  } = state;

  return (
    <>
      <Helmet>
        <title>{t('employee.title')}</title>
      </Helmet>
      <CompanyBreadcrumb companyId={companyId} items={breadcrumbItems} />
      <div>
        <h1 className="mb-2 text-lg font-semibold md:text-2xl">
          {t('employee.title')}
        </h1>
        <div
          className={cn(
            'flex flex-col gap-3',
            isPlaceholderData && 'opacity-50',
          )}
        >
          <div className="self-end flex gap-2">
            {hasEditPermission && (
              <PrintButton
                disabled={isLoading}
                variant="outline"
                onClick={() =>
                  print({
                    content: (
                      <DataTableHTML
                        columns={memoizedPrintColumns}
                        data={data?.data}
                        pagination={pagination}
                      />
                    ),
                  })
                }
              />
            )}
            <PrintEmployeeQrCardsButton
              employees={data?.data}
              restaurantId={restaurant.id}
              companyId={companyId}
              disabled={isLoading}
            />
            {hasEditPermission && (
              <>
                <Link
                  to={importTo}
                  className={buttonVariants({ variant: 'outline' })}
                >
                  {t('employee.import')}
                </Link>
                <Link
                  to={createTo}
                  className={buttonVariants({ variant: 'default' })}
                >
                  {t('employee.create')}
                </Link>
              </>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <SearchInput
              className="self-start p-0"
              placeholder={t('common.search-placeholder')}
              searchInput={searchInput}
              handleChange={handleSearchChange}
            />
            <DataTable
              data={data?.data}
              serverSideSorting
              sorting={sorting}
              setSorting={setSorting}
              columns={memoizedColumns}
              pageCount={data?.total_pages}
              pagination={pagination}
              isLoading={isLoading}
              setPagination={setPagination}
              columnVisibility={{
                edit: hasEditPermission,
              }}
            />
          </div>
        </div>
      </div>
      <ImportCompanyEmployeesModal />
      <CreateRestaurantCompanyEmployeeModal />
      {Boolean(employeeId) && (
        <EditRestaurantCompanyEmployeeModal key={employeeId} />
      )}
    </>
  );
};
